import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reviewsApi, goalsApi, competenciesApi, usersApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Save, Star, Users } from 'lucide-react';

const RatingSelector = ({ value, onChange, disabled }) => {
  const ratings = [
    { value: 1, label: '1 - Did Not Meet' },
    { value: 2, label: '2 - Partially Meets' },
    { value: 3, label: '3 - Meets Expectation' },
    { value: 4, label: '4 - Exceeds' },
    { value: 5, label: '5 - Outstanding' },
  ];

  return (
    <div className="flex gap-2">
      {ratings.map((rating) => (
        <button
          key={rating.value}
          type="button"
          onClick={() => !disabled && onChange(rating.value)}
          disabled={disabled}
          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
            value === rating.value
              ? rating.value >= 4 ? 'bg-green-500 text-white' :
                rating.value === 3 ? 'bg-yellow-500 text-white' :
                'bg-red-500 text-white'
              : 'bg-muted hover:bg-muted/80'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          title={rating.label}
        >
          {rating.value}
        </button>
      ))}
    </div>
  );
};

export default function MidYearReviewPage() {
  const { user, isManager, isAdmin, isHR } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [goals, setGoals] = useState([]);
  const [review, setReview] = useState(null);
  const [goalUpdates, setGoalUpdates] = useState({});
  const [feedback, setFeedback] = useState({
    what_went_well: '',
    what_could_be_better: '',
    employee_strengths: '',
    employee_opportunities: '',
  });
  
  // For managers reviewing team
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (isManager || isAdmin || isHR) {
      loadTeamMembers();
    } else {
      loadMyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTeamMembers = async () => {
    try {
      let response;
      if (isAdmin || isHR) {
        response = await usersApi.getAll();
      } else {
        response = await usersApi.getTeam(user.id);
      }
      setTeamMembers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load team:', error);
      toast.error('Failed to load team members');
      setLoading(false);
    }
  };

  const loadMyData = async () => {
    try {
      const goalsRes = await goalsApi.getAll({ year: currentYear });
      setGoals(goalsRes.data);
      
      // Initialize goal updates
      const updates = {};
      goalsRes.data.forEach(goal => {
        updates[goal.id] = {
          employee_update: goal.employee_update || '',
          manager_update: goal.manager_update || '',
        };
      });
      setGoalUpdates(updates);
      
      // Load or create review
      const reviewRes = await reviewsApi.create({
        employee_id: user.id,
        year: currentYear,
        stage: 'mid_year',
      });
      setReview(reviewRes.data);
      
      if (reviewRes.data.mid_year_feedback) {
        setFeedback(reviewRes.data.mid_year_feedback);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load review data');
    } finally {
      setLoading(false);
    }
  };

  const loadMemberData = async (memberId) => {
    setLoading(true);
    try {
      const goalsRes = await goalsApi.getAll({ employee_id: memberId, year: currentYear });
      setGoals(goalsRes.data);
      
      const updates = {};
      goalsRes.data.forEach(goal => {
        updates[goal.id] = {
          employee_update: goal.employee_update || '',
          manager_update: goal.manager_update || '',
        };
      });
      setGoalUpdates(updates);
      
      const reviewRes = await reviewsApi.create({
        employee_id: memberId,
        year: currentYear,
        stage: 'mid_year',
      });
      setReview(reviewRes.data);
      
      if (reviewRes.data.mid_year_feedback) {
        setFeedback(reviewRes.data.mid_year_feedback);
      } else {
        setFeedback({
          what_went_well: '',
          what_could_be_better: '',
          employee_strengths: '',
          employee_opportunities: '',
        });
      }
    } catch (error) {
      console.error('Failed to load member data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    loadMemberData(member.id);
  };

  const handleSaveGoalUpdate = async (goalId) => {
    setSaving(true);
    try {
      const updateField = isManager || isAdmin || isHR ? 'manager_update' : 'employee_update';
      await goalsApi.update(goalId, {
        [updateField]: goalUpdates[goalId][updateField],
      });
      toast.success('Update saved');
    } catch (error) {
      console.error('Failed to save update:', error);
      toast.error('Failed to save update');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeedback = async () => {
    if (!review) return;

    setSaving(true);
    try {
      // Goes through the manager-general-feedback endpoint (not the generic review
      // update) so the backend can enforce that KPI-level comments were already
      // submitted, and so it doesn't blow away manager_feedback / employee_feedback
      // by overwriting the whole mid_year_feedback object.
      const targetId = selectedMember ? selectedMember.id : user.id;
      await reviewsApi.submitManagerGeneralFeedback(targetId, currentYear, 'mid_year', feedback);
      toast.success('Feedback saved');
    } catch (error) {
      console.error('Failed to save feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to save feedback');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Manager/Admin/HR view with team selection
  if ((isManager || isAdmin || isHR) && !selectedMember) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="mid-year-review-page">
        <div>
          <h1 className="text-3xl font-bold">Mid-Year Review</h1>
          <p className="text-muted-foreground mt-1">
            Select a team member to conduct their mid-year review
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map((member) => (
                <button
                  key={member.id}
                  onClick={() => handleSelectMember(member)}
                  className="p-4 border rounded-lg hover:bg-muted/50 transition-colors text-left"
                  data-testid={`select-member-review-${member.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-medium text-primary">
                        {member.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-muted-foreground">{member.employee_number}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mid-year-review-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Mid-Year Review</h1>
          <p className="text-muted-foreground mt-1">
            {selectedMember ? `Reviewing: ${selectedMember.name}` : 'Update your progress on goals'}
          </p>
        </div>
        {selectedMember && (
          <Button variant="outline" onClick={() => setSelectedMember(null)}>
            Back to Team
          </Button>
        )}
      </div>

      <Tabs defaultValue="goals" className="space-y-6">
        <TabsList>
          <TabsTrigger value="goals">Goal Updates</TabsTrigger>
          {(isManager || isAdmin || isHR) && (
            <TabsTrigger value="feedback">Manager Feedback</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="goals" className="space-y-4">
          {goals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No goals found for this year</p>
              </CardContent>
            </Card>
          ) : (
            goals.map((goal) => (
              <Card key={goal.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{goal.title}</CardTitle>
                      <CardDescription>{goal.kpi}</CardDescription>
                    </div>
                    <Badge variant="outline">{goal.weight}% Weight</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Employee Update</Label>
                      <Textarea
                        value={goalUpdates[goal.id]?.employee_update || ''}
                        onChange={(e) => setGoalUpdates({
                          ...goalUpdates,
                          [goal.id]: { ...goalUpdates[goal.id], employee_update: e.target.value }
                        })}
                        placeholder="Describe your progress on this goal..."
                        disabled={isManager || isAdmin || isHR}
                        data-testid={`employee-update-${goal.id}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Manager Update</Label>
                      <Textarea
                        value={goalUpdates[goal.id]?.manager_update || ''}
                        onChange={(e) => setGoalUpdates({
                          ...goalUpdates,
                          [goal.id]: { ...goalUpdates[goal.id], manager_update: e.target.value }
                        })}
                        placeholder="Manager's assessment..."
                        disabled={!isManager && !isAdmin && !isHR}
                        data-testid={`manager-update-${goal.id}`}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => handleSaveGoalUpdate(goal.id)}
                      disabled={saving}
                      data-testid={`save-goal-update-${goal.id}`}
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Update
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {(isManager || isAdmin || isHR) && (
          <TabsContent value="feedback">
            <Card>
              <CardHeader>
                <CardTitle>Manager Feedback</CardTitle>
                <CardDescription>
                  Provide comprehensive feedback on the employee's performance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>What Went Well</Label>
                    <Textarea
                      value={feedback.what_went_well}
                      onChange={(e) => setFeedback({ ...feedback, what_went_well: e.target.value })}
                      placeholder="Highlight positive achievements..."
                      rows={4}
                      data-testid="feedback-went-well"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>What Could Have Been Done Better</Label>
                    <Textarea
                      value={feedback.what_could_be_better}
                      onChange={(e) => setFeedback({ ...feedback, what_could_be_better: e.target.value })}
                      placeholder="Areas for improvement..."
                      rows={4}
                      data-testid="feedback-could-be-better"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Employee Strengths</Label>
                    <Textarea
                      value={feedback.employee_strengths}
                      onChange={(e) => setFeedback({ ...feedback, employee_strengths: e.target.value })}
                      placeholder="Key strengths observed..."
                      rows={4}
                      data-testid="feedback-strengths"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Employee Opportunities</Label>
                    <Textarea
                      value={feedback.employee_opportunities}
                      onChange={(e) => setFeedback({ ...feedback, employee_opportunities: e.target.value })}
                      placeholder="Growth opportunities..."
                      rows={4}
                      data-testid="feedback-opportunities"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveFeedback} disabled={saving} data-testid="save-feedback-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Feedback
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
